import { t } from 'i18next';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { Flex } from '../../../components/Flex';
import { ImageUpload } from '../../../components/ImageUpload';
import { Section } from '../../../components/Section';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { Lengths } from '../../../model/shared/enum/Lengths.enum';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { LinkInput } from './LinkInput';
import { LinkTextarea } from './LinkTextarea';

const ProfileInputsWrapper = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  flex-wrap: wrap;
  justify-content: center;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    flex-wrap: nowrap;
    gap: ${Spacing.m};
  }
`;

const ImageUploadWrapper = styled(Flex)`
  .ant-upload-wrapper {
    border: ${({ theme }) => `1px solid ${theme.main}`} !important;
    border-radius: ${Rounded.circle} !important;
  }

  .ant-upload-select {
    border-radius: ${Rounded.circle} !important;
    border: 0 !important;
    overflow: hidden !important;
  }

  .ant-upload-list-picture-card {
    display: block !important;
    position: relative !important;
  }

  .ant-upload-list-item {
    border: 0 !important;
    padding: 0 !important;
    border-radius: ${Rounded.circle} !important;
    overflow: hidden !important;
  }

  .ant-upload-list-item-container {
    position: absolute !important;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }

  .ant-upload-list-item-actions {
    z-index: 5000 !important;
  }

  .ant-upload-list-item::before {
    width: 100% !important;
    height: 100% !important;
  }

  button {
    width: 100%;
    height: 100%;

    &:hover {
      background-color: ${({ theme }) => theme.lightMain} !important;
    }
  }
`;

export const AtiraLinkContentProfile: React.FC = () => {
  const { control, setValue, watch, clearErrors } =
    useFormContext<CreateAtiraLinkDto>();

  const image = watch('image');

  return (
    <Section title={t('common.profile')}>
      <ProfileInputsWrapper>
        <Controller
          control={control}
          name="image"
          render={({ field: { onChange } }) => (
            <ImageUploadWrapper>
              <ImageUpload
                maxCount={1}
                onRemove={() => setValue('image', '')}
                onChange={onChange}
                image={image || ''}
                dataTestId={atiraLinkTestIDs.content.image}
              />
            </ImageUploadWrapper>
          )}
        />

        <Flex flexDirection="column" gap="m" width={'100%'}>
          <Controller
            control={control}
            name="title"
            rules={{
              required: true,
              minLength: {
                value: Lengths.MIN_3,
                message: t('error.range', {
                  name: t('common.title'),
                  min: Lengths.MIN_3,
                  max: Lengths.PHONE,
                }),
              },
              maxLength: {
                value: Lengths.NAME,
                message: t('error.range', {
                  name: t('common.title'),
                  min: Lengths.MIN_3,
                  max: Lengths.NAME,
                }),
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <LinkInput
                title={t('common.title')}
                required
                value={value}
                onChange={onChange}
                placeholder={t('atira_link.create.content.title.placeholder')}
                errorMessage={error?.message}
                valid={!error}
                maxLength={Lengths.NAME}
                dataTestId={atiraLinkTestIDs.content.title}
                onFocus={() => clearErrors('title')}
              />
            )}
          />

          <Controller
            control={control}
            name="bio"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <LinkTextarea
                title={t('common.bio')}
                value={value}
                onChange={onChange}
                placeholder={t('atira_link.create.content.text.placeholder')}
                maxLength={Lengths.DESCRIPTION}
                dataTestId={atiraLinkTestIDs.content.bio}
                valid={!error}
                errorMessage={error?.message}
                onFocus={() => clearErrors('bio')}
              />
            )}
          />
        </Flex>
      </ProfileInputsWrapper>
    </Section>
  );
};
